import React from "react";
import ReactDOM from "react-dom";
import BaseComponent from "../../baseComponent.js";
import InternalEvent from "../../../core/constants/internalEvent.js";
import WidgetService from "../../../core/services/widgetService.js";
import WidgetType from "../../../core/constants/widgetType.js";
import BelongingComponent from "../../../core/constants/belongingComponent.js";
import WrapperWidget from "../widgets/wrapperWidget.jsx";
import UniqueIdGenerator from "../../../core/utils/uniqueIdGenerator.js";
import DashboardService from "../../../core/services/dashboardService.js";
import {variableTypes} from "../control-widget/selection-contorl/constants.js";

const GRID_DIMENSION_WIDTH = 30;
const GRID_DIMENSION_HEIGHT = 30;

export default class Grid extends BaseComponent {
  constructor(props) {
    super(props);
    this.gridster = null;
    this.maximizedWidgetId = null;
    this.state = {
      dashboardState: props.dashboardState
    };
    this.snapshotStatus = {};
  }

  componentDidMount() {
    this.initializeGridster(this.calcGridMinWidth());
    this.renderWidgets();
    this.subscribe(InternalEvent.Dashboard.CHART_RESET, payload => {
      this.resetWidget(payload, payload.maximized);
    });

    this.subscribe(InternalEvent.Dashboard.UPDATE_DASHBOARD_STATE, () => {
      this.onStateChange();
    });

    this.subscribe(InternalEvent.Dashboard.CONTROL_ADDED, (metaData) => {
      this.addWidget(UniqueIdGenerator.id(), metaData.title, WidgetType.CONTROL_WIDGET, [], metaData);
      this.onStateChange();
    });

    this.subscribe(InternalEvent.Dashboard.CONTROL_UPDATED, (payload) => {
      const widget = WidgetService.getWidget(payload.widgetId);
      this.resetWidget(widget, false);
      this.onStateChange();
    });

    this.subscribe(InternalEvent.Dashboard.DASHBOARD_LOCK_TOGGLED, () => {
      if (DashboardService.getActiveDashboardLockState()) {
        this.gridster.disable_resize();
      } else {
        this.gridster.enable_resize();
      }
    });

    this.subscribe(InternalEvent.Dashboard.WIDGET_SNAPSHOT_LOADED, data => {
      this.snapshotStatus[data.widgetId] = data.noSubscriptions || [];
      let notSubscribedDatasets = [];
      for (let widgetId in this.snapshotStatus) {
        if (!this.snapshotStatus[widgetId]) {
          continue;
        } else {
          notSubscribedDatasets = notSubscribedDatasets.concat(this.snapshotStatus[widgetId]);
        }
      }
      // console.log(this.snapshotStatus);
      this.publish(InternalEvent.Dashboard.OPEN_SUBSCRIPTION_POPUP, {map: this.snapshotStatus, notSubscribedDatasets})
    });

    this.subscribe(InternalEvent.Dashboard.DASHBOARD_VARIABLE_CHANGED, this.handleDashboardVariableUpdate);

    this.subscribe(InternalEvent.Dashboard.RESIZE_ALL_WIDGETS, this.resizeAllWidgets);

    this.subscribe(InternalEvent.Dashboard.ADD_VERTICAL_SEPARATOR_WIDGET, () => {
      this.addWidget(UniqueIdGenerator.id(), "", WidgetType.SEPARATOR_VERTICAL, [], {});
      this.onStateChange();
    });
  }

  /**
   * Updates dashboard with the new dashboard variable value and resets widgets which use the updated dashboard variable.
   * @param variables [{id, value}, {id, value}...]
   */
  handleDashboardVariableUpdate(variables) {
    let variablesToCheck = variables;
    variables.forEach(v => {
      variablesToCheck = [...variablesToCheck, ...this.findVariablesReferencedBy(v)];
    });
    this.props.onVariableUpdated(variables)
      .then(() => {
        const resetWidgetModels = [];
        this.state.dashboardState.dashboardState.forEach(state => {
          const widgetModel = state.widgetModel;
          widgetModel.legends.forEach(l => {
            variablesToCheck.forEach(v => {
              if ((l.expressions.y != null && l.expressions.y.indexOf(v.id) > -1) ||
                (l.DataSource && l.DataSource.indexOf(v.id) > -1)) {
                if (widgetModel.type !== WidgetType.CONTROL_WIDGET) {
                  const index = resetWidgetModels.findIndex(m => m.id === widgetModel.id);
                  if (index === -1) {
                    resetWidgetModels.push(widgetModel);
                  }
                }
              }
            });
          });

          Object.keys(widgetModel.metaData).forEach(key => {
            variablesToCheck.forEach(v => {
              const meta = widgetModel.metaData[key];
              if (meta && meta.type === variableTypes.EXPRESSION && meta.expression.indexOf("${" + v.name + "}") > -1) {
                resetWidgetModels.push(widgetModel);
              }
            });
          });
        });
        resetWidgetModels.forEach(widget => {
          this.resetWidget(widget, false);
        });
      });
  }

  // find all expression type variables associate with input variable
  findVariablesReferencedBy(variable) {
    const allVariables = DashboardService.getCurrentDashboardVariables();
    const variablesHaveReference = [];
    const allExpressionVariables = [];

    Object.keys(allVariables).forEach(key => {
      const variable = allVariables[key];
      if (variable.type === variableTypes.EXPRESSION) {
        allExpressionVariables.push(variable);
      }
    });

    allExpressionVariables.forEach(v => {
      if (v.expression && v.expression.indexOf("${" + variable.name + "}") > -1) {
        variablesHaveReference.push(v);
      }
    });
    return variablesHaveReference;
  }

  calcGridMinWidth() {
    const gridDimensions = this.getGridDimensions();
    let maxWidth = 0;
    this.state.dashboardState.dashboardState.map(state => {
      let gridsterModel = state.gridsterModel;
      if (gridsterModel) {
        let width = (gridsterModel.col + gridsterModel.size_x) * gridDimensions[0];
        if (width > maxWidth) {
          maxWidth = width;
        }
      }
    });
    return maxWidth;
  }

  renderWidgets() {
    let delay = 0;
    this.state.dashboardState.dashboardState.forEach(state => {
      let widgetModel = state.widgetModel;
      let payload = {
        widgetId: widgetModel.id,
        title: widgetModel.title,
        type: widgetModel.type,
        legends: widgetModel.legends,
        metaData: widgetModel.metaData,
        autoAdjustWindowSize: widgetModel.autoAdjustWindowSize,
        centerZeroLine: widgetModel.centerZeroLine,
        splitLeftRight: widgetModel.splitLeftRight
      };

      let newWidget = WidgetService.createRealtimeWidget(payload);
      if (state.gridsterModel == null) {
        this.addGridsterWidget(newWidget);
      }

      this.snapshotStatus[widgetModel.id] = false;
      setTimeout(() => {
        this.renderWidget(newWidget, widgetModel.components);
      }, delay++ * 10);
    });

    setTimeout(() => {
      this.publish(InternalEvent.App.HIDE_PRELOADER);
    }, 500);
  }

  addWidget(widgetId, title, type, legends, metaData) {
    let payload = {
      widgetId: widgetId,
      title: title,
      type: type,
      legends: legends,
      metaData: metaData
    };
    let newWidget = WidgetService.createRealtimeWidget(payload);
    this.addGridsterWidget(newWidget);
    this.renderWidget(newWidget);
  }

  renderWidget(widget, components) {
    ReactDOM.render(React.createElement(
      WrapperWidget,
      {
        widget: widget,
        model: widget,
        components: components,
        dashboardId: this.dashboardId,
        onStateChange: () => this.onStateChange(),
        onWidgetEdit: this.handleWidgetEdit,
        onWidgetCopy: this.handleWidgetCopy,
        onWidgetRemove: this.removeWidget,
        toggleMaximize: this.toggleMaximizeWidget,
        onSnapshotLoaded: this.handleWidgetSnapshotLoaded,
        isPreviewWidget: false
      }
    ), document.getElementById(widget.id));
  }

  handleWidgetSnapshotLoaded() {
    // console.log("handleWidgetSnapshotLoaded")
  }

  getWidgetDefaultDimensions(type, title) {
    let dimensions;
    switch (type) {
      case WidgetType.TIME_SERIES_VERTICAL:
      case WidgetType.TIME_SERIES: {
        dimensions = {width: 10, height: 5};
        return dimensions;
      }
      case WidgetType.MAP:
      case WidgetType.TREE_MAP:
      case WidgetType.PIE_CHART: {
        dimensions = {width: 10, height: 8};
        return dimensions;
      }
      case WidgetType.TEXTUAL: {
        if (title.length > 0) {
          dimensions = {width: 3, height: 2};
        } else {
          dimensions = {width: 3, height: 1};
        }
        return dimensions;
      }
      case WidgetType.SPREADSHEET:
      case WidgetType.GAUGE: {
        dimensions = {width: 7, height: 5};
        return dimensions;
      }
      case WidgetType.TABLE: {
        dimensions = {width: 14, height: 5};
        return dimensions;
      }
      case WidgetType.CONTROL_WIDGET: {
        dimensions = {width: 3, height: 2, maxHeight: 2, minHeight: 2};
        return dimensions;
      }
      case WidgetType.TRADING_VIEW:
      case WidgetType.TECHNICAL_ANALYSIS_CHART: {
        dimensions = {width: 14, height: 10};
        return dimensions;
      }
      case WidgetType.RANK_WIDGET:
      case WidgetType.BULLET_VERTICAL: {
        dimensions = {width: 3, height: 5};
        return dimensions;
      }
      case WidgetType.SEPARATOR_VERTICAL: {
        dimensions = {width: 1, height: 5};
        return dimensions;
      }
      default: {
        // it's easy for developers to forget to set minimum dimensions.
        // So to prevent bugs, throw an exception here.
        throw "Minimum dimension has to be set for the adding widget";
      }
    }
  }

  componentWillUnmount() {
    // console.log("grid unmount");
    this.unsubscribeAll();
    this.removeAllWidgets();
    $(".gridster > ul").removeData().unbind();
    this.gridster.destroy();
    WidgetService.reset(BelongingComponent.DASHBOARD);
  }

  removeAllWidgets() {
    // Remove all widgets from widget store and unsubscribe data stream if needed
    var gridsterWidgets = this.gridster.$widgets;
    for (var i = 0; i < gridsterWidgets.length; i++) {
      var widgetId = gridsterWidgets[i].firstChild.getAttribute("id");
      ReactDOM.unmountComponentAtNode(document.getElementById(widgetId));
      WidgetService.removeWidget(widgetId);
    }
  }

  resetWidget(widget, isMaximized) {
    this.removeWidget(widget.id, false);
    let newWidget = WidgetService.createRealtimeWidget({
      widgetId: widget.id,
      type: widget.type,
      title: widget.title,
      legends: widget.legends,
      metaData: widget.metaData,
      belongingComponent: BelongingComponent.DASHBOARD,
      autoAdjustWindowSize: widget.autoAdjustWindowSize,
      centerZeroLine: widget.centerZeroLine,
      splitLeftRight: widget.splitLeftRight
    });


    ReactDOM.render(React.createElement(
      WrapperWidget,
      {
        widget: newWidget,
        model: newWidget,
        components: widget.components,
        dashboardId: this.dashboardId,
        onStateChange: () => this.onStateChange(),
        onWidgetEdit: this.handleWidgetEdit,
        onWidgetRemove: this.removeWidget,
        onWidgetCopy: this.handleWidgetCopy,
        isMaximized: isMaximized,
        toggleMaximize: this.toggleMaximizeWidget,
        isPreviewWidget: false
      }
    ), document.getElementById(newWidget.id));
  }

  removeWidget(widgetId, removeGridster = true) {
    ReactDOM.unmountComponentAtNode(document.getElementById(widgetId));
    WidgetService.removeWidget(widgetId);

    if (removeGridster) {
      this.gridster.remove_widget($("#" + widgetId).parent());
      this.props.onWidgetChange(this.state.dashboardState.persistenceId, this.gridster.serialize());
    }
  }

  addGridsterWidget(widget) {
    let html = `<li class="widget noselect"><div id="${widget.id}" style="height: 100%;"></div><div class="widget-size"></div></li>`;
    let defaultDimension = this.getWidgetDefaultDimensions(widget.type, widget.title);
    const gridsterModel = {
      size_x: defaultDimension.width,
      size_y: defaultDimension.height,
      min_size_x: defaultDimension.minWidth,
      min_size_y: defaultDimension.minHeight
    };

    if (defaultDimension.maxHeight) {
      gridsterModel.max_size_y = defaultDimension.maxHeight;
    }

    this.gridster.add_widget(
      html,
      gridsterModel.size_x,
      gridsterModel.size_y,
      gridsterModel.col || null,
      gridsterModel.row || null,
      null,
      [defaultDimension.minWidth, defaultDimension.minHeight]
    );
  }

  onStateChange() {
    this.props.onWidgetChange(this.state.dashboardState.persistenceId, this.gridster.serialize());
  }

  getGridDimensions() {
    let baseDimensions = [GRID_DIMENSION_WIDTH, GRID_DIMENSION_HEIGHT];
    const dashboardVariables = DashboardService.getCurrentDashboardVariables();
    if (dashboardVariables.gridBaseDimensions) {
      baseDimensions = [dashboardVariables.gridBaseDimensions.value.width, dashboardVariables.gridBaseDimensions.value.height];
    }
    return baseDimensions;
  }

  initializeGridster(minWidth, resizeAll) {
    let wrapperWidth = $("#gridster-wrapper").width() - 120;
    if (minWidth > wrapperWidth) {
      $(".mainGrid").css("min-width", minWidth);
    }

    this.gridster = $(".mainGrid > ul").gridster(
      {
        namespace: '#mainGrid',
        widget_margins: [0, 0],
        widget_base_dimensions: this.getGridDimensions(),
        min_rows: 50,
        resize: {
          enabled: true,
          start: (e, ui, $widget) => {
            this.applyWidgetBorder();
            this.showWidgetSize();
            $("#resizeGrid").hide();
          },
          resize: (e, ui, $widget) => {
            this.publish(InternalEvent.Dashboard.WIDGET_RESIZE, {widget: $widget});
            this.updateWidgetSize($widget);
          },
          stop: (e, ui, $widget) => {
            this.resetWidgetBorder();
            this.publish(InternalEvent.Dashboard.WIDGET_RESIZE_STOP, {widget: $widget});
            $(window).resize();
            this.hideWidgetSize();
            this.onStateChange();
            $("#resizeGrid").show();
          }
        },
        draggable: {
          start: (event, ui) => {
            this.applyWidgetBorder();
            this.showWidgetSize();
            $("#resizeGrid").hide();
          },
          stop: (event, ui) => {
            this.resetWidgetBorder();
            this.onStateChange();
            this.hideWidgetSize();
            this.gridster.disable();
            $("#resizeGrid").show();
          }
        },
        serialize_params: ($widget, widgetData) => {
          let widgetId = $($widget[0].childNodes[0]).attr('id');
          let widgetModel = WidgetService.getWidget(widgetId);
          return {
            gridsterModel: {
              col: widgetData.col,
              row: widgetData.row,
              size_x: widgetData.size_x,
              size_y: widgetData.size_y
            },
            widgetModel: widgetModel
          };
        }
      }).data('gridster');
    this.gridster.disable();
    if (DashboardService.getActiveDashboardLockState()) {
      this.gridster.disable_resize();
    }

    if (resizeAll) {
      setTimeout(() => {
        for (let i = 0; i < this.gridster.$widgets.length; i++) {
          this.publish(InternalEvent.Dashboard.WIDGET_RESIZE_STOP, {widget: $(this.gridster.$widgets[i])})
        }
      }, 100);
    }
  }

  showWidgetSize() {
    let allWidgets = $("li.gs-w");
    for (let i = 0; i < allWidgets.length; i++) {
      let $currentWidget = $(allWidgets[i]);
      const sizeX = $currentWidget.attr("data-sizex");
      const sizeY = $currentWidget.attr("data-sizey");
      const $widgetSize = $currentWidget.find(".widget-size");
      $widgetSize.text(`${sizeX} X ${sizeY}`);
      $widgetSize.show();
    }
  }

  updateWidgetSize($widget) {
    const sizeX = $widget.attr("data-sizex");
    const sizeY = $widget.attr("data-sizey");
    const $widgetSize = $widget.find(".widget-size");
    $widgetSize.text(`${sizeX} x ${sizeY}`);
  }

  hideWidgetSize() {
    let allWidgets = $("li.gs-w");
    for (let i = 0; i < allWidgets.length; i++) {
      let $currentWidget = $(allWidgets[i]);
      const $widgetSize = $currentWidget.find(".widget-size");
      $widgetSize.hide();
    }
  }

  handleWidgetEdit(editingWidget) {
    WidgetService.setAddWidgetType(editingWidget.type);
    this.props.router.push("/editWidget/" + editingWidget.id);
  }

  handleWidgetCopy(copyWidget) {
    WidgetService.setAddWidgetType(copyWidget.type);
    this.props.router.push("/copyWidget/" + copyWidget.id);
  }

  openAddWidgetDialog(widgetType) {
    WidgetService.setAddWidgetType(widgetType);
    this.props.router.replace("/addWidget");
  }

  toggleMaximizeWidget(event) {
    const dimensions = this.getGridDimensions();
    var widgetContainer = $(event.target).parents("li");
    var widgetId = $(event.target).parents("div[data-widget-id]").attr("data-widget-id");
    var width = widgetContainer.attr("data-sizex") * dimensions[0];
    var height = widgetContainer.attr("data-sizey") * dimensions[1];

    if (this.maximizedWidgetId === widgetId) {
      this.maximizedWidgetId = null;

      $(widgetContainer).css({
        "position": "",
        "top": "",
        "left": "",
        "margin-left": "",
        "margin-top": "",
        "width": "",
        "height": "",
        "z-index": "",
        "padding": ""
      });
      $(widgetContainer).removeClass("maximized");

      $(".icon-size-actual")
        .removeClass("icon-size-actual")
        .addClass("icon-size-fullscreen");

      this.publish(InternalEvent.Dashboard.WIDGET_RESIZE, {
        widget: widgetContainer,
        width: width,
        height: height,
        isFromToggleWidgetSize: true,
        maximized: false
      });
    } else {
      this.maximizedWidgetId = widgetId;
      var offset = $(widgetContainer).offset();
      var padding = ($(window).width() < 600 || $(window).height() < 600) ? "0 5%" : "2% 5%";
      this.originalZIndex = widgetContainer.css("z-index");

      $(widgetContainer).css({
        "position": "fixed",
        "top": "55px",
        "left": offset.left,
        "margin-left": -offset.left,
        "width": "100%",
        "height": "calc(100% - 55px)",
        "z-index": 1000,
        "padding": padding
      });
      $(widgetContainer).addClass("maximized");


      $(".icon-size-fullscreen")
        .removeClass("icon-size-fullscreen")
        .addClass("icon-size-actual");

      this.publish(InternalEvent.Dashboard.WIDGET_RESIZE, {
        widget: widgetContainer,
        width: $(window).width() * 0.9,
        height: ($(window).height() - 100) * 0.8,
        isFromToggleWidgetSize: true,
        maximized: true
      });
    }
  }

  applyWidgetBorder() {
    const border = "1px solid #D8D8D8";
    const allWidgets = $("li.gs-w");
    for (let i = 0; i < allWidgets.length; i++) {
      let $currentWidget = $(allWidgets[i]);
      $currentWidget.css({
        "border-top": border,
        "border-left": border,
        "border-right": border
      });
    }
  }

  resetWidgetBorder() {
    const allWidgets = $("li.gs-w");
    for (let i = 0; i < allWidgets.length; i++) {
      let $currentWidget = $(allWidgets[i]);
      $currentWidget.css({
        "border-top": "none",
        "border-left": "none",
        "border-right": "none"
      });
    }
  }

  resizeAllWidgets({width, height}) {
    let maxX = 0, maxY = 0;
    const allWidgets = this.gridster.$widgets;
    for (let i = 0; i < allWidgets.length; i++) {
      let $currentWidget = $(allWidgets[i]);
      let x = parseInt($currentWidget.attr('data-sizex'));
      let y = parseInt($currentWidget.attr('data-sizey'));
      let r = parseInt($currentWidget.attr('data-row'));
      let c = parseInt($currentWidget.attr('data-col'));
      if (x + c > maxX) {
        maxX = x + c - 1;
      }
      if (y + r > maxY) {
        maxY = y + r - 1;
      }
    }

    const newBaseDimensionWidth = width / maxX;
    const newBaseDimensionHeight = height / maxY;

    this.props.updateDashboardVariable([{
      id: "gridBaseDimensions",
      value: {
        width: newBaseDimensionWidth,
        height: newBaseDimensionHeight
      }
    }]).then(() => {
      this.gridster.destroy();
      this.initializeGridster(this.calcGridMinWidth(), true);
    });

  }

  render() {
    // rendering gridster widgets in react render function increases rendering performance
    const widgets = this.state.dashboardState.dashboardState.map(state => {
      const widgetModel = state.widgetModel;
      const gridsterModel = state.gridsterModel;
      const defaultDimensions = this.getWidgetDefaultDimensions(state.widgetModel.type, state.widgetModel.title);

      if (gridsterModel) {
        // if widget is newly created, it needs to be added by the gridster instance to get the proper position
        return <li className="widget noselect" key={widgetModel.id}
                   data-row={gridsterModel.row} data-col={gridsterModel.col}
                   data-sizex={gridsterModel.size_x} data-sizey={gridsterModel.size_y}
                   data-min-sizex={defaultDimensions.minWidth} data-min-sizey={defaultDimensions.minHeight}>
          <div id={widgetModel.id} style={{height: "100%"}}/>
          <div className="widget-size"/>
        </li>;
      }
    });

    return (
      <div id="mainGrid" className="mainGrid gridster">
        <ul>
          {widgets}
        </ul>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/dashboard/grid/grid.jsx